import { useEffect, useRef } from "react";

import { Loader2 } from "lucide-react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/utils/ui";

/** One speaker-grouped turn in the live diarized feed. */
export type LiveTranscriptionSegment = {
  speaker: string;
  text: string;
  /** Pre-formatted start timestamp, e.g. "00:12:48". */
  timestamp: string;
};

type LiveTranscriptionFeedProps = {
  segments: LiveTranscriptionSegment[];
  /**
   * The in-progress (interim) hypothesis for the current turn — rendered greyed
   * with a caret cursor + "…" beneath the finalised turns. Omitted once the
   * turn finalises into `segments`.
   */
  interim?: LiveTranscriptionSegment | null;
  /**
   * Drives the loading/status footer under the feed: "connecting" (session
   * handshake), "restoring" (re-hydrating the feed after returning to the tab
   * mid-session), "paused" (transcription suspended), or "listening" (recording
   * but no interim yet — e.g. the gap right after resume, so it doesn't look
   * frozen). Omit/"idle" to show nothing.
   */
  state?: "idle" | "connecting" | "restoring" | "listening" | "paused";
};

/**
 * The diarized live-transcription feed shown in the Scribe session panel
 * (MDRS-116). Finalised turns render speaker (semibold) + body with a
 * right-aligned timestamp; the interim line is greyed and trailed by a blinking
 * caret cursor and "…". Auto-scrolls to the newest turn.
 */
export const LiveTranscriptionFeed = ({
  segments,
  interim,
  state = "idle",
}: LiveTranscriptionFeedProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Keep the newest turn in view as the assessment is transcribed.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [segments, interim, state]);

  const isEmpty = segments.length === 0 && !interim;
  // Show the status footer only when there's no interim line already conveying
  // activity — and never while idle.
  const showStatus = state !== "idle" && !interim;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Full-bleed subheader bar — matches the card header treatment. */}
      <div className="flex shrink-0 items-center justify-between border-y border-border bg-background-raised px-5 py-3">
        <h3 className="text-sm font-semibold text-background-dark">
          Live transcription
        </h3>
        {/* Auto-scrolling pill — primary-50 bg, primary text. */}
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-[10px] font-medium text-primary">
          <span className="size-1.5 rounded-full bg-primary" aria-hidden="true" />
          Auto-scrolling
        </span>
      </div>

      <ScrollArea className="min-h-0 flex-1 overflow-hidden bg-background-white">
        <div className="flex min-h-full flex-col gap-3 px-4 py-4">
          {isEmpty ? (
            <FeedEmptyState state={state} />
          ) : (
            <>
              {segments.map((segment, index) => (
                <FeedRow
                  key={`${segment.speaker}-${index}-${segment.timestamp}`}
                  segment={segment}
                />
              ))}
              {interim && <FeedRow segment={interim} isInterim />}
              {showStatus && <FeedStatus state={state} />}
            </>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    </div>
  );
};

const FeedRow = ({
  segment,
  isInterim = false,
}: {
  segment: LiveTranscriptionSegment;
  isInterim?: boolean;
}) => (
  <div className={cn("space-y-0.5", isInterim && "text-foreground-muted")}>
    <div className="flex items-baseline justify-between gap-3">
      <span
        className={cn(
          "text-xs font-semibold",
          isInterim ? "text-foreground-muted" : "text-foreground"
        )}
      >
        {segment.speaker}:
      </span>
      <span className="shrink-0 text-[10px] text-foreground-muted">
        {segment.timestamp}
      </span>
    </div>
    <p
      className={cn(
        "text-xs",
        isInterim ? "text-foreground-muted" : "text-foreground"
      )}
    >
      {segment.text}
      {isInterim && (
        <>
          {/* Caret cursor (1.5px × 13px primary bar) + trailing "…". */}
          <span
            className="ml-0.5 inline-block h-[13px] w-[1.5px] animate-pulse bg-primary align-middle"
            aria-hidden="true"
          />
          <span className="text-foreground-muted"> …</span>
        </>
      )}
    </p>
  </div>
);

// Centered empty state before any turn has landed. Idle (pre-session) shows a
// static hint; an active session shows a live "Connecting…/Listening…/Paused"
// affordance so the very start of a session reads as working, not blank.
const FeedEmptyState = ({
  state,
}: {
  state: "idle" | "connecting" | "restoring" | "listening" | "paused";
}) => {
  if (state === "idle") {
    return (
      <p className="text-xs italic text-foreground-muted">
        Speaker-labelled turns will appear here as the assessment is recorded.
      </p>
    );
  }
  const label =
    state === "connecting"
      ? "Connecting to transcription…"
      : state === "restoring"
        ? "Restoring transcription…"
        : state === "paused"
          ? "Paused — resume to continue transcribing"
          : "Listening for speech…";
  return (
    <div
      className="flex flex-1 flex-col items-center justify-center gap-3 py-10 text-center"
      role="status"
      aria-live="polite"
    >
      {state === "paused" ? (
        <span
          className="size-2.5 rounded-full bg-foreground-muted"
          aria-hidden="true"
        />
      ) : (
        <Loader2 className="size-6 animate-spin text-primary" aria-hidden="true" />
      )}
      <p className="text-sm text-foreground-muted">{label}</p>
    </div>
  );
};

// Loading / status line under the feed so the gaps (connecting, or the pause
// after resume) read as "working" rather than a frozen transcript.
const FeedStatus = ({
  state,
}: {
  state: "connecting" | "restoring" | "listening" | "paused";
}) => {
  const label =
    state === "connecting"
      ? "Connecting to transcription…"
      : state === "restoring"
        ? "Restoring transcription…"
        : state === "paused"
          ? "Paused — transcription will resume when you continue"
          : "Listening…";
  return (
    <div
      className="flex items-center gap-2 text-xs text-foreground-muted"
      role="status"
      aria-live="polite"
    >
      {state === "paused" ? (
        <span
          className="size-1.5 rounded-full bg-foreground-muted"
          aria-hidden="true"
        />
      ) : (
        <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
      )}
      {label}
    </div>
  );
};
